import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { UploadCloud, File as FileIcon, X, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { fmtSize } from "@/lib/formatters";

const ACCEPTED = {
  "application/pdf": [".pdf"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
};
const MAX_SIZE = 15 * 1024 * 1024;

const FileList = ({ files, onRemove }) => {
  const { t } = useLanguage();
  if (files.length === 0) return null;
  return (
    <div className="mt-4" data-testid="upload-file-list">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--muted-ink)]">{t.portal.filesSelected} ({files.length})</p>
      <ul className="space-y-2">
        {files.map((f, i) => (
          <li key={f.name + i} className="flex items-center gap-3 rounded-xl border border-[color:var(--border-subtle)] bg-white px-3 py-2.5">
            <FileIcon className="h-5 w-5 shrink-0 text-[var(--brand-burgundy)]" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-[var(--brand-charcoal)]">{f.name}</p>
              <p className="text-xs text-[var(--muted-ink)]">{fmtSize(f.size)}</p>
            </div>
            <button type="button" onClick={() => onRemove(i)} className="rounded-md p-1 text-[var(--muted-ink)] transition-colors hover:bg-black/5 hover:text-[var(--danger)]" aria-label={t.portal.removeFile} data-testid={`remove-file-${i}`}>
              <X className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const DocumentUpload = ({ files, onAddFiles, onRemoveFile }) => {
  const { t } = useLanguage();

  const onDrop = useCallback((accepted, rejected) => {
    (rejected || []).forEach((r) => {
      const reason = r.errors?.[0]?.code === "file-too-large" ? "> 15MB" : "";
      toast.error(`${r.file.name} ${reason}`.trim());
    });
    if (accepted && accepted.length) onAddFiles(accepted);
  }, [onAddFiles]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop, accept: ACCEPTED, maxSize: MAX_SIZE, noClick: true, noKeyboard: true,
  });

  return (
    <div className="border-b border-[color:var(--border-subtle)] p-6 sm:p-8 lg:border-b-0 lg:border-r">
      <h3 className="font-heading text-xl font-semibold text-[var(--brand-navy)]">{t.portal.uploadTitle}</h3>
      <p className="mt-1.5 text-sm text-[var(--muted-ink)]">{t.portal.uploadSubtext}</p>

      <div
        {...getRootProps()}
        className={`mt-5 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed px-5 py-10 text-center transition-colors ${
          isDragActive
            ? "border-[var(--brand-burgundy)] bg-[rgba(139,30,63,0.06)]"
            : "border-[color:var(--border-strong)] bg-[var(--surface-2)]"
        }`}
        data-testid="upload-dropzone"
      >
        <input
          {...getInputProps()}
          id="document-upload"
          name="documents"
          aria-label={t.portal.uploadAriaLabel}
          data-testid="upload-input"
        />
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-[var(--shadow-sm)]">
          <UploadCloud className="h-7 w-7 text-[var(--brand-burgundy)]" />
        </div>
        <p className="mt-3 text-sm font-semibold text-[var(--brand-charcoal)]">
          {isDragActive ? t.portal.uploadDrop : t.portal.uploadSubtext}
        </p>
        <Button type="button" onClick={open} variant="outline" className="mt-4 h-10 rounded-xl border-[color:var(--border-strong)] bg-white font-semibold" data-testid="upload-browse">
          {t.portal.uploadCta}
        </Button>
        <p className="mt-3 text-xs text-[var(--muted-ink)]">{t.portal.uploadConstraints}</p>
      </div>

      <FileList files={files} onRemove={onRemoveFile} />

      <div className="mt-4 flex items-center gap-2 text-xs text-[var(--muted-ink)]">
        <ShieldCheck className="h-4 w-4 text-[var(--success)]" />
        {t.portal.uploadSecure}
      </div>
    </div>
  );
};
